import PropTypes from 'prop-types';
import {compose, withHandlers, setPropTypes} from 'recompose';
import {withRouter} from 'react-router-dom';
import ensureBlankRow from './handlers/ensureBlankRow';
import submitTimesheet from './handlers/submitTimesheet';
import approveTimesheet from './handlers/approveTimesheet';
import submitAndApproveTimesheet from './handlers/submitAndApproveTimesheet';
import copyPreviousWeek from './handlers/copyPreviousWeek';
import recallTimesheetSubmission from './handlers/recallTimesheetSubmission';
import deleteRow from './handlers/deleteRow';
import addRow from './handlers/addRow';
import deleteEntry from './handlers/deleteEntry';
import updateRow from './handlers/updateRow';
import updateTime from './handlers/updateTime';
import flushPendingSaves from './handlers/flushPendingSaves';
import updateNarrative from './handlers/updateNarrative';
import saveOvernightComp from './handlers/saveOvernightComp';
import saveInLieu from './handlers/saveInLieu';
import saveOnCall from './handlers/saveOnCall';
import saveTravelPay from './handlers/saveTravelPay';
import updatePercentComplete from './handlers/updatePercentComplete';
import updateOpOutlook from './handlers/updateOpOutlook';

// Wraps a per-timesheet mutation handler so any queued cell edits (accumulated
// while auto-save was off) are flushed first.  This prevents the common
// footgun of a user submitting / approving / recalling before clicking the
// batched Save button and losing those pending edits.
//
// Copy Last Week uses a different pattern: the outer copyPreviousWeek /
// copyPreviousWeekWithTime handlers only show the confirmation dialog, and
// the actual mutation (copyPreviousWeekMutation) is invoked by the dialog's
// successAction once the user confirms.  Wrapping the mutation handler here
// keeps the flush-on-confirm behaviour while letting the confirmation
// dialog show BEFORE any flush - so cancelling the copy no longer silently
// commits queued edits.  The auto-save OFF path routes to
// queueCopyPreviousWeek instead, which bypasses the flush entirely.
const withAutoFlush = withHandlers({
  submitTimesheet: ({submitTimesheet, flushPendingSaves}) => async dates => {
    if (flushPendingSaves) await flushPendingSaves();
    return submitTimesheet(dates);
  },
  approveTimesheet: ({approveTimesheet, flushPendingSaves}) => async dates => {
    if (flushPendingSaves) await flushPendingSaves();
    return approveTimesheet(dates);
  },
  submitAndApproveTimesheet: ({
    submitAndApproveTimesheet,
    flushPendingSaves
  }) => async dates => {
    if (flushPendingSaves) await flushPendingSaves();
    return submitAndApproveTimesheet(dates);
  },
  recallTimesheetSubmission: ({
    recallTimesheetSubmission,
    flushPendingSaves
  }) => async dates => {
    if (flushPendingSaves) await flushPendingSaves();
    return recallTimesheetSubmission(dates);
  },
  copyPreviousWeekMutation: ({
    copyPreviousWeekMutation,
    flushPendingSaves
  }) => async includeTime => {
    if (flushPendingSaves) await flushPendingSaves();
    return copyPreviousWeekMutation(includeTime);
  }
});

export default compose(
  setPropTypes({
    timesheet: PropTypes.object.isRequired
  }),
  withRouter,
  withHandlers({
    goToTimesheet: ({history, onTimesheetNavigate}) => date => {
      onTimesheetNavigate(date.format('YYYY-MM-DD'));
      history.push('/timesheet/' + date.format('YYYY-MM-DD'));
    },
    // updates that only affect the Graphql cache.
    // we do this, rather than modifying the data in Redux, because it lets us only have one copy of the timesheet,
    // this makes it easy to provide an optimistic UI just using the default Apollo capabilities
    addRow
  }),
  ensureBlankRow,
  // graphql mutations
  submitTimesheet,
  approveTimesheet,
  submitAndApproveTimesheet,
  copyPreviousWeek,
  recallTimesheetSubmission,
  deleteRow,
  updateRow,
  updateTime,
  flushPendingSaves,
  deleteEntry,
  updateNarrative,
  saveOvernightComp,
  saveInLieu,
  saveOnCall,
  saveTravelPay,
  updatePercentComplete,
  updateOpOutlook,
  // Must be innermost so it can see the mutation handlers and flushPendingSaves
  // in ownProps and then re-export wrapped versions to the component.
  withAutoFlush
);
